import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";

const STATUS = {
  prospect: { label: "Prospect", color: "border-zinc-600 text-zinc-300" },
  pilote: { label: "Pilote", color: "border-sky-500/40 text-sky-300" },
  actif: { label: "Actif", color: "border-emerald-500/40 text-emerald-300" },
  pause: { label: "En pause", color: "border-amber-500/40 text-amber-300" },
};

const EMPTY_FORM = {
  name: "", school: "", city: "", status: "prospect", formula: "", cadence: "",
  start_date: "", email: "", phone: "", disciplines: "", profiles_granted: 0,
  next_meeting_date: "", notes: "",
};

export default function PartnersModule() {
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("infos");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [portal, setPortal] = useState(null); // { link, name }

  const load = async () => {
    try {
      const { data } = await api.get("/partners");
      setPartners(data.partners || []);
      setStats(data.stats || null);
      if (!selectedId && data.partners?.length) setSelectedId(data.partners[0].id);
    } catch (e) {
      toast.error("Erreur de chargement des partenaires");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const selected = partners.find((p) => p.id === selectedId) || null;

  const openCreate = () => { setForm(EMPTY_FORM); setEditingId(null); setEditOpen(true); };
  const openEdit = (p) => {
    setForm({ ...EMPTY_FORM, ...p, profiles_granted: p.profiles_granted || 0, start_date: p.start_date || "", next_meeting_date: p.next_meeting_date || "" });
    setEditingId(p.id); setEditOpen(true);
  };

  const savePartner = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    const payload = { ...form, profiles_granted: parseInt(form.profiles_granted) || 0 };
    try {
      if (editingId) {
        const { data } = await api.patch(`/partners/${editingId}`, payload);
        toast.success("Partenaire mis à jour");
        setSelectedId(data.id);
      } else {
        const { data } = await api.post("/partners", payload);
        toast.success("Partenaire ajouté");
        setSelectedId(data.id);
      }
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    }
  };

  const deletePartner = async (p) => {
    if (!window.confirm(`Supprimer le partenaire « ${p.name} » ?`)) return;
    try {
      await api.delete(`/partners/${p.id}`);
      toast.success("Partenaire supprimé");
      setSelectedId(null);
      await load();
    } catch (e) { toast.error("Erreur suppression"); }
  };

  const openContract = async (p) => {
    try {
      const resp = await api.get(`/partners/${p.id}/contrat`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      await load(); // rafraîchit l'historique des conventions
      toast.success("Convention générée et archivée");
    } catch (e) { toast.error("Impossible de générer la convention PDF"); }
  };

  const openBilan = async (p) => {
    try {
      const year = new Date().getFullYear();
      const resp = await api.get(`/partners/${p.id}/bilan-annuel?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error("Impossible de générer le bilan annuel"); }
  };

  const copyPortalLink = async (p) => {
    try {
      const { data } = await api.post(`/partners/${p.id}/portal-token`);
      const link = `${window.location.origin}${data.path}`;
      setPortal({ link, name: p.name });
    } catch (e) { toast.error("Impossible de générer le lien portail"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="partners-module">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.4em] text-amber-400 uppercase">Réseau Kaizen</div>
        <h1 className="text-3xl font-bold text-zinc-100 tracking-tight">🤝 Partenaires & franchisés</h1>
        <p className="text-zinc-500 text-sm mt-1">Gère tes écoles affiliées : contrats, rencontres et suivi des élèves de chaque partenaire.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <StatCard label="Partenaires" value={stats.total} />
          <StatCard label="Actifs" value={stats.actifs} accent="emerald" />
          <StatCard label="Pilotes" value={stats.pilotes} accent="sky" />
          <StatCard label="Élèves suivis" value={stats.students_total} />
          <StatCard label="Conv. signées" value={stats.signed ?? 0} accent="emerald" />
          <StatCard label="À faire signer" value={stats.unsigned ?? 0} accent="amber" />
        </div>
      )}

      <div className="grid md:grid-cols-[300px_1fr] gap-6">
        {/* Liste */}
        <div>
          <Button onClick={openCreate} className="w-full mb-3 bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="add-partner-btn">+ Nouveau partenaire</Button>
          <div className="space-y-2">
            {partners.map((p) => {
              const st = STATUS[p.status] || STATUS.prospect;
              return (
                <button
                  key={p.id}
                  onClick={() => { setSelectedId(p.id); setTab("infos"); }}
                  data-testid={`partner-item-${p.id}`}
                  className={`w-full text-left rounded-xl border p-3 transition-colors ${selectedId === p.id ? "border-amber-500/60 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/50 hover:border-zinc-700"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-zinc-100 truncate">{p.name}</div>
                    <Badge variant="outline" className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">{p.school || "—"}{p.city ? ` · ${p.city}` : ""}</div>
                  <div className="text-[11px] text-zinc-600 mt-1">📋 {p.summary?.meetings_count || 0} rencontre(s) · 🥋 {p.summary?.students_count || 0} élève(s)</div>
                  {p.next_meeting_date && <div className="text-[11px] text-sky-400 mt-0.5" data-testid={`next-visit-${p.id}`}>📅 Prochaine visite : {p.next_meeting_date}</div>}
                  <div className="mt-1.5">
                    {p.summary?.has_signed_contract
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300" data-testid={`signed-badge-${p.id}`}>✅ Convention signée</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-300" data-testid={`unsigned-badge-${p.id}`}>✍️ À faire signer</span>}
                  </div>
                </button>
              );
            })}
            {partners.length === 0 && <div className="text-sm text-zinc-500 text-center py-6">Aucun partenaire.</div>}
          </div>
        </div>

        {/* Détail */}
        <div>
          {!selected ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-10 text-center text-zinc-500">Sélectionne un partenaire.</div>
          ) : (
            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-5" data-testid="partner-detail">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-2xl font-bold text-zinc-100">{selected.name}</h2>
                  <div className="text-sm text-zinc-400">{selected.school}{selected.city ? ` · ${selected.city}` : ""}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" onClick={() => openContract(selected)} data-testid="contract-pdf-btn">📄 Convention PDF</Button>
                  <Button size="sm" variant="outline" className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10" onClick={() => openBilan(selected)} data-testid="bilan-annuel-btn">📊 Bilan annuel</Button>
                  <Button size="sm" variant="outline" className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10" onClick={() => copyPortalLink(selected)} data-testid="portal-link-btn">🔗 Lien portail</Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(selected)} data-testid="edit-partner-btn">✏️ Modifier</Button>
                  <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => deletePartner(selected)} data-testid="delete-partner-btn">🗑️</Button>
                </div>
              </div>

              <div className="flex gap-2 mt-4 border-b border-zinc-900" data-testid="partner-tabs">
                {[["infos", "Infos"], ["meetings", `Rencontres (${selected.summary?.meetings_count || 0})`], ["students", `Élèves affiliés (${selected.summary?.students_count || 0})`], ["contracts", `Conventions (${(selected.contracts || []).length})`]].map(([k, l]) => (
                  <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                    className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{l}</button>
                ))}
              </div>

              <div className="pt-4">
                {tab === "infos" && <InfoTab p={selected} />}
                {tab === "meetings" && <MeetingsTab p={selected} onChange={load} setPartners={setPartners} />}
                {tab === "students" && <StudentsTab p={selected} onChange={load} setPartners={setPartners} />}
                {tab === "contracts" && <ContractsTab p={selected} onChange={load} onGenerate={() => openContract(selected)} />}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal créer/éditer */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[85vh] overflow-auto" data-testid="partner-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Modifier le partenaire" : "Nouveau partenaire"}</DialogTitle>
            <DialogDescription className="text-zinc-500">Informations de l'école affiliée / du franchisé.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom *" className="col-span-2"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="f-name" /></Field>
            <Field label="École"><Input value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} data-testid="f-school" /></Field>
            <Field label="Ville"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="f-city" /></Field>
            <Field label="Statut">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="f-status"
                className="w-full h-10 rounded-md bg-zinc-900 border border-zinc-800 px-3 text-sm text-zinc-100">
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Cadence rencontres"><Input value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })} placeholder="1×/mois" data-testid="f-cadence" /></Field>
            <Field label="Formule / redevance" className="col-span-2"><Input value={form.formula} onChange={(e) => setForm({ ...form, formula: e.target.value })} placeholder="500 $/an · 400 $/visite…" data-testid="f-formula" /></Field>
            <Field label="Date de début"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="f-start" /></Field>
            <Field label="Prochaine visite"><Input type="date" value={form.next_meeting_date} onChange={(e) => setForm({ ...form, next_meeting_date: e.target.value })} data-testid="f-next-meeting" /></Field>
            <Field label="Profils élèves accordés"><Input type="number" value={form.profiles_granted} onChange={(e) => setForm({ ...form, profiles_granted: e.target.value })} data-testid="f-profiles" /></Field>
            <Field label="Courriel"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="f-email" /></Field>
            <Field label="Téléphone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="f-phone" /></Field>
            <Field label="Disciplines couvertes" className="col-span-2"><Input value={form.disciplines} onChange={(e) => setForm({ ...form, disciplines: e.target.value })} placeholder="Can-Ryu…" data-testid="f-disciplines" /></Field>
            <Field label="Notes / mentorat" className="col-span-2"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="f-notes" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black" onClick={savePartner} data-testid="save-partner-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portail partenaire — lien + QR imprimable */}
      <Dialog open={!!portal} onOpenChange={(o) => !o && setPortal(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="portal-qr-dialog">
          <DialogHeader>
            <DialogTitle>🔗 Portail de {portal?.name}</DialogTitle>
            <DialogDescription className="text-zinc-500">Lien en lecture seule à partager au franchisé (ses élèves + ses conventions).</DialogDescription>
          </DialogHeader>
          {portal && (
            <div className="space-y-4">
              <div id="portal-qr-print" className="bg-white rounded-xl p-5 flex flex-col items-center gap-2">
                <QRCodeCanvas value={portal.link} size={220} level="M" includeMargin data-testid="portal-qr-canvas" />
                <div className="text-black text-xs font-semibold text-center">Portail partenaire · {portal.name}</div>
                <div className="text-zinc-500 text-[10px] text-center">Institut Impact Élite Martial × Kaizen System</div>
              </div>
              <div className="text-xs text-zinc-400 break-all bg-zinc-900 border border-zinc-800 rounded-lg p-2" data-testid="portal-link-text">{portal.link}</div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-amber-500 hover:bg-amber-400 text-black" data-testid="portal-copy-btn"
                  onClick={async () => { try { await navigator.clipboard.writeText(portal.link); toast.success("Lien copié"); } catch (e) { toast.error("Copie impossible"); } }}>📋 Copier</Button>
                <Button size="sm" variant="outline" className="flex-1" data-testid="portal-open-btn" onClick={() => window.open(portal.link, "_blank")}>↗ Ouvrir</Button>
                <Button size="sm" variant="outline" className="flex-1" data-testid="portal-print-btn" onClick={() => {
                  const node = document.getElementById("portal-qr-print");
                  const w = window.open("", "_blank", "width=520,height=640");
                  if (!w) { toast.error("Autorise les pop-ups pour imprimer."); return; }
                  w.document.write(`<html><head><title>QR Portail — ${portal.name}</title></head><body style="display:flex;justify-content:center;padding:40px;">${node.outerHTML}</body></html>`);
                  w.document.close(); w.focus(); setTimeout(() => { try { w.print(); } catch (e) {} }, 300);
                }}>🖨️ Imprimer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}

function InfoTab({ p }) {
  const rows = [
    ["Formule / redevance", p.formula],
    ["Cadence rencontres", p.cadence],
    ["Date de début", p.start_date],
    ["Prochaine visite prévue", p.next_meeting_date],
    ["Disciplines couvertes", p.disciplines],
    ["Profils élèves accordés", p.profiles_granted],
    ["Courriel", p.email],
    ["Téléphone", p.phone],
  ];
  return (
    <div className="space-y-3" data-testid="info-tab">
      <div className="grid md:grid-cols-2 gap-2">
        {rows.map(([k, v]) => (
          <div key={k} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">{k}</div>
            <div className="text-sm text-zinc-200 mt-0.5">{v || "—"}</div>
          </div>
        ))}
      </div>
      {p.notes && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="text-[10px] tracking-[0.2em] text-amber-400 uppercase mb-1">Notes / mentorat</div>
          <p className="text-sm text-zinc-200 whitespace-pre-wrap">{p.notes}</p>
        </div>
      )}
    </div>
  );
}

function MeetingsTab({ p, onChange }) {
  const [open, setOpen] = useState(false);
  const [m, setM] = useState({ date: "", summary: "", next_actions: "" });

  const add = async () => {
    if (!m.summary.trim()) { toast.error("Résumé requis"); return; }
    try {
      await api.post(`/partners/${p.id}/meetings`, m);
      toast.success("Rencontre ajoutée");
      setOpen(false); setM({ date: "", summary: "", next_actions: "" });
      await onChange();
    } catch (e) { toast.error("Erreur"); }
  };
  const del = async (mid) => {
    if (!window.confirm("Supprimer ce compte-rendu ?")) return;
    try { await api.delete(`/partners/${p.id}/meetings/${mid}`); await onChange(); } catch (e) { toast.error("Erreur"); }
  };

  const meetings = [...(p.meetings || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return (
    <div data-testid="meetings-tab">
      <Button size="sm" onClick={() => setOpen(true)} className="mb-3 bg-amber-500 hover:bg-amber-400 text-black" data-testid="add-meeting-btn">+ Ajouter un compte-rendu</Button>
      <div className="space-y-2">
        {meetings.map((mt) => (
          <div key={mt.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3" data-testid={`meeting-${mt.id}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-amber-300">📅 {mt.date || "Sans date"}</div>
              <button onClick={() => del(mt.id)} className="text-xs text-red-400 hover:underline" data-testid={`del-meeting-${mt.id}`}>Supprimer</button>
            </div>
            <p className="text-sm text-zinc-200 mt-1 whitespace-pre-wrap">{mt.summary}</p>
            {mt.next_actions && <p className="text-xs text-zinc-400 mt-2"><span className="text-emerald-400">→ Prochaines actions :</span> {mt.next_actions}</p>}
          </div>
        ))}
        {meetings.length === 0 && <div className="text-sm text-zinc-500 text-center py-6">Aucune rencontre enregistrée.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="meeting-form-dialog">
          <DialogHeader><DialogTitle>Compte-rendu de rencontre</DialogTitle>
            <DialogDescription className="text-zinc-500">Note datée de la rencontre avec le partenaire.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Date"><Input type="date" value={m.date} onChange={(e) => setM({ ...m, date: e.target.value })} data-testid="m-date" /></Field>
            <Field label="Résumé *"><Textarea rows={4} value={m.summary} onChange={(e) => setM({ ...m, summary: e.target.value })} data-testid="m-summary" /></Field>
            <Field label="Prochaines actions"><Textarea rows={2} value={m.next_actions} onChange={(e) => setM({ ...m, next_actions: e.target.value })} data-testid="m-actions" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black" onClick={add} data-testid="save-meeting-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StudentsTab({ p, onChange }) {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState({ name: "", belt: "", discipline: "", progress_pct: 0, notes: "" });
  const [editingId, setEditingId] = useState(null);

  const openNew = () => { setS({ name: "", belt: "", discipline: "", progress_pct: 0, notes: "" }); setEditingId(null); setOpen(true); };
  const openEdit = (st) => { setS({ ...st }); setEditingId(st.id); setOpen(true); };

  const save = async () => {
    if (!s.name.trim()) { toast.error("Nom requis"); return; }
    const payload = { ...s, progress_pct: parseInt(s.progress_pct) || 0 };
    try {
      if (editingId) await api.patch(`/partners/${p.id}/students/${editingId}`, payload);
      else await api.post(`/partners/${p.id}/students`, payload);
      toast.success("Élève enregistré");
      setOpen(false); await onChange();
    } catch (e) { toast.error("Erreur"); }
  };
  const del = async (sid) => {
    if (!window.confirm("Retirer cet élève ?")) return;
    try { await api.delete(`/partners/${p.id}/students/${sid}`); await onChange(); } catch (e) { toast.error("Erreur"); }
  };

  const students = p.students || [];
  return (
    <div data-testid="students-tab">
      <Button size="sm" onClick={openNew} className="mb-3 bg-amber-500 hover:bg-amber-400 text-black" data-testid="add-student-btn">+ Ajouter un élève affilié</Button>
      <div className="space-y-2">
        {students.map((st) => (
          <div key={st.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3" data-testid={`student-${st.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-semibold text-zinc-100">{st.name}</div>
                <div className="text-xs text-zinc-500">{[st.belt, st.discipline].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-amber-300">{st.progress_pct}%</span>
                <button onClick={() => openEdit(st)} className="text-xs text-sky-400 hover:underline" data-testid={`edit-student-${st.id}`}>✏️</button>
                <button onClick={() => del(st.id)} className="text-xs text-red-400 hover:underline" data-testid={`del-student-${st.id}`}>🗑️</button>
              </div>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, st.progress_pct)}%` }} />
            </div>
            {st.notes && <p className="text-xs text-zinc-400 mt-2">{st.notes}</p>}
          </div>
        ))}
        {students.length === 0 && <div className="text-sm text-zinc-500 text-center py-6">Aucun élève affilié suivi.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="student-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Modifier l'élève" : "Élève affilié"}</DialogTitle>
            <DialogDescription className="text-zinc-500">Suivi manuel d'un élève de l'école partenaire.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom *" className="col-span-2"><Input value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} data-testid="s-name" /></Field>
            <Field label="Ceinture"><Input value={s.belt} onChange={(e) => setS({ ...s, belt: e.target.value })} data-testid="s-belt" /></Field>
            <Field label="Discipline"><Input value={s.discipline} onChange={(e) => setS({ ...s, discipline: e.target.value })} data-testid="s-discipline" /></Field>
            <Field label="Progression (%)" className="col-span-2"><Input type="number" min="0" max="100" value={s.progress_pct} onChange={(e) => setS({ ...s, progress_pct: e.target.value })} data-testid="s-pct" /></Field>
            <Field label="Notes" className="col-span-2"><Textarea rows={2} value={s.notes} onChange={(e) => setS({ ...s, notes: e.target.value })} data-testid="s-notes" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black" onClick={save} data-testid="save-student-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContractsTab({ p, onChange, onGenerate }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const contracts = [...(p.contracts || [])];

  const toggleSigned = async (c) => {
    try {
      await api.patch(`/partners/${p.id}/contrat/${c.id}/signed?signed=${!c.signed}`);
      await onChange();
    } catch (e) { toast.error("Erreur"); }
  };
  const del = async (cid) => {
    if (!window.confirm("Retirer cette convention de l'historique ?")) return;
    try { await api.delete(`/partners/${p.id}/contrat/${cid}`); await onChange(); } catch (e) { toast.error("Erreur"); }
  };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("fr-CA"); } catch (e) { return iso; } };

  return (
    <div data-testid="contracts-tab">
      <Button size="sm" onClick={onGenerate} className="mb-3 bg-emerald-600 hover:bg-emerald-500" data-testid="generate-contract-btn">📄 Générer une nouvelle convention</Button>
      <div className="space-y-2">
        {contracts.map((c) => (
          <div key={c.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 flex items-center justify-between gap-2" data-testid={`contract-${c.id}`}>
            <div>
              <div className="text-sm text-zinc-100">📄 Convention · {fmtDate(c.generated_at)}</div>
              {c.signed && <div className="text-[11px] text-emerald-400">✅ Signée{c.signed_at ? ` · ${fmtDate(c.signed_at)}` : ""}</div>}
            </div>
            <div className="flex items-center gap-2">
              <a href={`${BACKEND}${c.url}`} target="_blank" rel="noreferrer" className="text-xs text-sky-400 hover:underline" data-testid={`open-contract-${c.id}`}>Ouvrir</a>
              <button onClick={() => toggleSigned(c)} className={`text-xs hover:underline ${c.signed ? "text-zinc-400" : "text-emerald-400"}`} data-testid={`sign-contract-${c.id}`}>{c.signed ? "Marquer non signée" : "Marquer signée"}</button>
              <button onClick={() => del(c.id)} className="text-xs text-red-400 hover:underline" data-testid={`del-contract-${c.id}`}>🗑️</button>
            </div>
          </div>
        ))}
        {contracts.length === 0 && <div className="text-sm text-zinc-500 text-center py-6">Aucune convention générée. Clique « Générer une nouvelle convention » pour créer et archiver la première version.</div>}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  const colors = { emerald: "text-emerald-300", sky: "text-sky-300", amber: "text-amber-300" };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3">
      <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${colors[accent] || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-zinc-400">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
